/// <reference types="google.accounts" />
import { useCallback, useEffect, useState } from "react";

import { reportError } from "@/v1/sentry";
import { getConfig } from "~/config";
import { useScript } from "./useScript";

const GOOGLE_GSI_URL = "https://accounts.google.com/gsi/client";
const CLIENT_ID = getConfig("GOOGLE_CLIENT_ID");
const IS_LOCAL = import.meta.env.VITE_APP_ENV === "development";

interface CredentialResponse {
  credential: string;
  select_by: string;
}

/**
 * Reasons google.accounts.id.prompt() can report through
 * notification.getNotDisplayedReason() / getSkippedReason() when the One
 * Tap moment ends without a credential. Most of these are expected user or
 * browser behaviour (dismissed the prompt, blocked third-party cookies,
 * unsupported browser, ...), not application faults, so SMT-328 stops
 * reporting them as Sentry errors and only logs them locally. A short list
 * stays "actionable": a real integration/config fault (bad or missing
 * client_id), or a reason vague enough ("unknown_reason") that it could be
 * masking one - Sentry issue BACKOFFICE-E8 is 8,800+ events of exactly that.
 *
 * See: https://developers.google.com/identity/gsi/web/reference/js-reference#PromptMomentNotification
 */
const ACTIONABLE_ONE_TAP_REASONS = new Set<string>([
  // client_id sent to google.accounts.id.initialize() is invalid, or
  // wasn't recognised by Google at all - a real configuration fault.
  "invalid_client",
  // client_id wasn't sent to initialize() - a real configuration fault.
  "missing_client_id",
  // The page wasn't served over HTTPS - a deployment/config fault, not user behaviour.
  "secure_http_required",
  // The origin isn't registered for this client_id in Google Cloud Console - a config fault.
  "unregistered_origin",
  // Google's own catch-all for "none of the documented reasons apply".
  // Kept reporting: it is the dominant source of BACKOFFICE-E8's volume,
  // and an undocumented reason could be hiding a real fault.
  "unknown_reason",
]);

// FIXME: Present alternative login if the user by accident clicks "cancel"
// or skips the moment - see: google.accounts.id.renderButton (SMT-1011,
// out of scope for SMT-328).
const handlePromptMomentNotification = (
  notification: google.accounts.id.PromptMomentNotification
) => {
  if (!notification.isNotDisplayed() && !notification.isSkippedMoment()) {
    return;
  }

  const notDisplayedReason = notification.isNotDisplayed()
    ? notification.getNotDisplayedReason()
    : undefined;
  const skippedReason = notification.isSkippedMoment()
    ? notification.getSkippedReason()
    : undefined;
  // Real-world case (BACKOFFICE-E8): Google has reported "unknown_reason"
  // through the skipped-moment branch even though the shipped types only
  // document it on getNotDisplayedReason(). Fall back to it, and include
  // both reasons in the message below, so the report stays diagnosable
  // regardless of which getter Google actually populates.
  const reason = notDisplayedReason ?? skippedReason ?? "unknown_reason";
  const message = `[useGoogleOneTap]: not_displayed=${
    notDisplayedReason ?? "n/a"
  } skipped=${skippedReason ?? "n/a"}`;

  if (ACTIONABLE_ONE_TAP_REASONS.has(reason)) {
    reportError(new Error(message), { tags: { oneTapReason: reason } });
    return;
  }

  // eslint-disable-next-line no-console
  console.warn(message);
};

export const useGoogleOneTap = () => {
  const [scriptAttached, setScriptAttached] = useState<boolean>(false);
  const scriptStatus = useScript(GOOGLE_GSI_URL);
  const [credentialResponse, setCredentialResponse] =
    useState<CredentialResponse | null>(null);

  useEffect(() => {
    if (scriptStatus === "ready" && window.google && !scriptAttached) {
      google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: setCredentialResponse,
        auto_select: true,
        use_fedcm_for_prompt: !IS_LOCAL,
        cancel_on_tap_outside: false,
      });

      setScriptAttached(true);
    }
  }, [scriptStatus, scriptAttached]);

  const promptOneTap = useCallback(() => {
    if (!scriptAttached && !window.google) return;

    google.accounts.id.prompt(handlePromptMomentNotification);
  }, [scriptAttached]);

  return {
    promptOneTap,
    credentialResponse,
  };
};
